import { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  Paper,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Link,
  styled,
} from '@mui/material';

interface PricingPowerItem {
  asin: string;
  sku: string;
  score: number;
  buybox_pct: number;
  competitors: number;
  current_price: number;
  lowest_offer: number;
}

type Order = 'asc' | 'desc';
type SortableColumn = keyof PricingPowerItem;

interface ColumnDef {
  id: SortableColumn;
  label: string;
  numeric: boolean;
}

const COLUMNS: ColumnDef[] = [
  { id: 'score', label: 'Score', numeric: true },
  { id: 'sku', label: 'SKU', numeric: false },
  { id: 'buybox_pct', label: 'Buy Box %', numeric: true },
  { id: 'competitors', label: 'Comp.', numeric: true },
  { id: 'current_price', label: 'Price', numeric: true },
  { id: 'lowest_offer', label: 'Lowest Offer', numeric: true },
];

// Match the styling of the Orders table at "/" (TerratreeTable).
const StyledTableContainer = styled(TableContainer)({
  width: '100%',
  '& .MuiTableCell-root': {
    padding: '8px 4px',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
});

const StyledTableCell = styled(TableCell)({
  padding: '8px 4px',
  fontSize: '12px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  borderBottom: 'none',
  color: '#555',
  whiteSpace: 'nowrap',
  '&.MuiTableCell-head': {
    fontWeight: 500,
    color: '#666',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
});

const StyledTableRow = styled(TableRow)({
  '&:hover': { backgroundColor: 'rgba(0, 123, 255, 0.06)' },
  '& td:first-of-type': { borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' },
  '& td:last-of-type': { borderTopRightRadius: '4px', borderBottomRightRadius: '4px' },
});

function scoreChipColor(score: number): 'success' | 'warning' | 'default' {
  if (score >= 8) return 'success';
  if (score >= 5) return 'warning';
  return 'default';
}

export default function PricingPowerTable() {
  const [items, setItems] = useState<PricingPowerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<SortableColumn>('score');
  const [order, setOrder] = useState<Order>('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    async function fetchData() {
      try {
        const result: any = await API.graphql({ query: '{ getPricingPower }' });
        const data = JSON.parse(result.data.getPricingPower);
        if (!Array.isArray(data)) {
          throw new Error('Unexpected response shape from getPricingPower');
        }
        setItems(data);
      } catch (e) {
        console.error('Failed to fetch pricing power data:', e);
        setError(e instanceof Error ? e.message : 'Failed to load pricing power data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleRequestSort = (property: SortableColumn) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const filtered = items.filter(item =>
    !search ||
    (item.sku ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (item.asin ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = a[orderBy] ?? 0;
    const bv = b[orderBy] ?? 0;
    let cmp: number;
    if (typeof av === 'string' || typeof bv === 'string') {
      cmp = String(av).localeCompare(String(bv));
    } else {
      cmp = (av as number) - (bv as number);
    }
    return order === 'desc' ? -cmp : cmp;
  });

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ mt: 4, p: 3, bgcolor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 1 }}>
        <Typography variant="h6" color="error" gutterBottom>Pricing Power unavailable</Typography>
        <Typography variant="body2" color="error">{error}</Typography>
      </Box>
    );
  }
  if (!items.length) {
    return (
      <Box sx={{ mt: 4, p: 3, textAlign: 'center', color: 'text.secondary' }}>
        No pricing power data available.
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, px: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Pricing Power</Typography>
        <TextField
          size="small"
          label="Search SKU or ASIN"
          variant="outlined"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 260 }}
        />
      </Box>
      <StyledTableContainer>
        <Paper elevation={0}>
        <Table style={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <StyledTableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleRequestSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map(item => (
              <StyledTableRow key={item.asin}>
                <StyledTableCell align="right">
                  <Chip
                    label={(item.score ?? 0).toFixed(1)}
                    size="small"
                    color={scoreChipColor(item.score ?? 0)}
                    sx={{ fontWeight: 700 }}
                  />
                </StyledTableCell>
                <StyledTableCell sx={{ fontFamily: 'monospace' }}>
                  <Link
                    href={`https://www.amazon.com/dp/${item.asin}`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    {item.sku}
                  </Link>
                </StyledTableCell>
                <StyledTableCell align="right">{((item.buybox_pct ?? 0) * 100).toFixed(1)}%</StyledTableCell>
                <StyledTableCell align="right">{item.competitors ?? 0}</StyledTableCell>
                <StyledTableCell align="right">${(item.current_price ?? 0).toFixed(2)}</StyledTableCell>
                <StyledTableCell align="right">
                  {item.lowest_offer > 0 ? `$${item.lowest_offer.toFixed(2)}` : '—'}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Items per page:"
        />
        </Paper>
      </StyledTableContainer>
    </Box>
  );
}
